import { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { DEFAULT_AVATAR } from "../utils/avatar";
import { api } from "../utils/api";
import { getSessionAuth } from "../utils/auth";

// Add your new user attributes here
const initialAttributes = {
    username: "",
    firstName: "",
    lastName: "",
    favoriteColor: "",
    picture: DEFAULT_AVATAR,
    bio: "",
    darkMode: true,
    favoriteGames: [],
    quizCompleted: false,
    language: "en",
};

const userReducer = (state, action) => {
    switch (action.type) {
        case "UPDATE_ATTRIBUTES":
            return { ...state, ...action.payload };
        case "SET_ATTRIBUTES":
            return { ...state, ...action.payload };
        default:
            return state;
    }
};

const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [state, dispatch] = useReducer(userReducer, initialAttributes);
    const { token: userToken, userId } = getSessionAuth();

    const fetchUser = useCallback(async () => {
        if (!userToken || !userId) return null;

        try {
            const result = await api.get(`/users/${userId}`);
            if (result) {
                dispatch({
                    type: "SET_ATTRIBUTES",
                    payload: {
                        ...initialAttributes,
                        ...(result.attributes || {}),
                    },
                });
            }
            return result;
        } catch (err) {
            console.log(err);
            return null;
        }
    }, [userId, userToken]);

    useEffect(() => {
        if (userToken) {
            fetchUser();
        }
    }, [fetchUser, userToken]);

    // Import this function for updating user attributes
    const updateAttributes = async (updatedFields = {}) => {
        if (!userId) return;

        try {
            await api.patch(`/users/${userId}`, {
                attributes: { ...state, ...updatedFields },
            });
            dispatch({ type: "UPDATE_ATTRIBUTES", payload: updatedFields });
        } catch (err) {
            console.log(err);
        }
    };

    const addFavoriteGame = async (gameId) => {
        if (!state.favoriteGames.includes(gameId)) {
            const updatedGames = [...state.favoriteGames, gameId];
            await updateAttributes({ favoriteGames: updatedGames });
        }
    };
    
    const removeFavoriteGame = async (gameId) => {
        const updatedGames = state.favoriteGames.filter(id => id !== gameId);
        await updateAttributes({ favoriteGames: updatedGames });
    };

    return (
        <UserContext.Provider value={{ user: state, fetchUser, updateAttributes, addFavoriteGame, removeFavoriteGame }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
